import { isObject, safeReadObject } from "../../utilities/object";
import { safeReadNumber } from "../../utilities/number";
import { safeReadString } from "../../utilities/string";
import { convertTemperature } from "../../utilities/temperature";
import type { TemperatureUnit } from "../../utilities/temperature";

import type { IFieldBase, IFieldTypes } from "./field";

export type ClimateUnitSystem = "METRIC" | "IMPERIAL";
export type ClimateTemperatureUnit = TemperatureUnit;
export const CLIMATE_ATTRIBUTION = "Source: Includes weather data from Google";

export const CLIMATE_METRIC_LABELS = {
  temperature: "Temperature",
  feelsLikeTemperature: "Feels like",
  airPressureMillibars: "Pressure",
  relativeHumidity: "Humidity",
  dewPoint: "Dew point",
  heatIndex: "Heat index",
  windChill: "Wind chill",
  wind: "Wind",
  windGust: "Wind gust",
  visibility: "Visibility",
  cloudCover: "Cloud cover",
  precipitation: "Precipitation",
  precipitationProbability: "Precipitation chance",
  precipitationType: "Precipitation type",
  thunderstormProbability: "Thunderstorm chance",
  uvIndex: "UV index",
  isDaytime: "Day / night",
};

export type ClimateMetric = keyof typeof CLIMATE_METRIC_LABELS;

export interface ClimateMetricSelection {
  metric: ClimateMetric;
  unitSystem?: ClimateUnitSystem;
}

export const CLIMATE_METRICS_WITH_UNITS: readonly ClimateMetric[] = [
  "temperature",
  "feelsLikeTemperature",
  "dewPoint",
  "heatIndex",
  "windChill",
  "wind",
  "windGust",
  "visibility",
  "precipitation",
];

export const DEFAULT_CLIMATE_METRICS: readonly ClimateMetricSelection[] = [
  { metric: "temperature", unitSystem: "METRIC" },
  { metric: "feelsLikeTemperature", unitSystem: "METRIC" },
  { metric: "relativeHumidity" },
];

export const CLIMATE_METRIC_OPTIONS = Object.entries(CLIMATE_METRIC_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export interface ClimateTemperature {
  degrees: number;
  unit: ClimateTemperatureUnit;
}

export interface ClimateQuantity {
  value: number;
  unit: string;
}

export interface ClimateValue {
  /** UTC timestamp set by our server when parsing the API response, not when the form is submitted. */
  capturedAt: string;
  /** Google's UTC timestamp associated with the returned conditions, not our request start time or necessarily the sensor observation time. */
  currentTime: string;
  location: {
    latitude: number;
    longitude: number;
  };
  /** IANA timezone returned by Google in timeZone.id. */
  timeZone?: string;
  isDaytime?: boolean;
  condition?: {
    type?: string;
    description?: string;
    iconBaseUri?: string;
  };
  temperature: ClimateTemperature;
  feelsLikeTemperature?: ClimateTemperature;
  dewPoint?: ClimateTemperature;
  heatIndex?: ClimateTemperature;
  windChill?: ClimateTemperature;
  relativeHumidity?: number;
  uvIndex?: number;
  thunderstormProbability?: number;
  airPressureMillibars?: number;
  cloudCover?: number;
  precipitation?: {
    probabilityPercent?: number;
    type?: string;
    quantity?: ClimateQuantity;
  };
  wind?: {
    directionDegrees?: number;
    directionCardinal?: string;
    speed?: ClimateQuantity;
    gust?: ClimateQuantity;
  };
  visibility?: ClimateQuantity;
}

export type IFieldClimate = IFieldBase & {
  fieldType: IFieldTypes.climate;
  autoCapture?: boolean;
  /** Display selection only; all supported current measurements remain stored. */
  selectedMetrics?: ClimateMetricSelection[];
};

function parseTemperature(value: unknown): ClimateTemperature | undefined {
  if (!isObject(value)) {
    return undefined;
  }

  const degrees = safeReadNumber(value, "degrees");
  const unit = safeReadString(value, "unit");
  if (degrees === undefined || (unit !== "CELSIUS" && unit !== "FAHRENHEIT")) {
    return undefined;
  }

  return { degrees, unit };
}

function parseQuantity(
  value: unknown,
  valueKey: "value" | "quantity" | "distance" = "value",
): ClimateQuantity | undefined {
  if (!isObject(value)) {
    return undefined;
  }

  const quantity = safeReadNumber(value, valueKey);
  const unit = safeReadString(value, "unit");
  if (quantity === undefined || unit === undefined) {
    return undefined;
  }

  return { value: quantity, unit };
}

export function parseGoogleClimateValue(
  response: unknown,
  location: ClimateValue["location"],
  capturedAt = new Date().toISOString(),
): ClimateValue | undefined {
  if (!isObject(response)) {
    return undefined;
  }

  const condition = safeReadObject(response, "weatherCondition");
  const precipitation = safeReadObject(response, "precipitation");
  const wind = safeReadObject(response, "wind");
  const visibility = safeReadObject(response, "visibility");

  // Adapt Google's nested properties once; saved values and API responses
  // then share the same validation and normalization.
  return normalizeClimateValue({
    ...response,
    capturedAt,
    location,
    timeZone: safeReadString(response, "timeZone.id"),
    condition: condition
      ? {
          type: safeReadString(condition, "type"),
          description: safeReadString(condition, "description.text"),
          iconBaseUri: safeReadString(condition, "iconBaseUri"),
        }
      : undefined,
    airPressureMillibars: safeReadNumber(
      response,
      "airPressure.meanSeaLevelMillibars",
    ),
    precipitation: precipitation
      ? {
          probabilityPercent: safeReadNumber(
            precipitation,
            "probability.percent",
          ),
          type: safeReadString(precipitation, "probability.type"),
          quantity: parseQuantity(precipitation.qpf, "quantity"),
        }
      : undefined,
    wind: wind
      ? {
          directionDegrees: safeReadNumber(wind, "direction.degrees"),
          directionCardinal: safeReadString(wind, "direction.cardinal"),
          speed: wind.speed,
          gust: wind.gust,
        }
      : undefined,
    visibility: parseQuantity(visibility, "distance"),
  });
}

export function normalizeClimateValue(
  value: unknown,
): ClimateValue | undefined {
  if (!isObject(value)) {
    return undefined;
  }

  const latitude = safeReadNumber(value, "location.latitude");
  const longitude = safeReadNumber(value, "location.longitude");
  if (
    latitude === undefined ||
    Math.abs(latitude) > 90 ||
    longitude === undefined ||
    Math.abs(longitude) > 180
  ) {
    return undefined;
  }

  const capturedAt = safeReadString(value, "capturedAt");
  const currentTime = safeReadString(value, "currentTime");
  const temperature = parseTemperature(value.temperature);
  if (
    capturedAt === undefined ||
    !Number.isFinite(Date.parse(capturedAt)) ||
    currentTime === undefined ||
    !Number.isFinite(Date.parse(currentTime)) ||
    temperature === undefined
  ) {
    return undefined;
  }

  const condition = safeReadObject(value, "condition");
  const precipitation = safeReadObject(value, "precipitation");
  const wind = safeReadObject(value, "wind");

  return {
    capturedAt,
    currentTime,
    location: { latitude, longitude },
    timeZone: safeReadString(value, "timeZone"),
    isDaytime:
      typeof value.isDaytime === "boolean" ? value.isDaytime : undefined,
    condition: condition
      ? {
          type: safeReadString(condition, "type"),
          description: safeReadString(condition, "description"),
          iconBaseUri: safeReadString(condition, "iconBaseUri"),
        }
      : undefined,
    temperature,
    feelsLikeTemperature: parseTemperature(value.feelsLikeTemperature),
    dewPoint: parseTemperature(value.dewPoint),
    heatIndex: parseTemperature(value.heatIndex),
    windChill: parseTemperature(value.windChill),
    relativeHumidity: safeReadNumber(value, "relativeHumidity"),
    uvIndex: safeReadNumber(value, "uvIndex"),
    thunderstormProbability: safeReadNumber(value, "thunderstormProbability"),
    airPressureMillibars: safeReadNumber(value, "airPressureMillibars"),
    cloudCover: safeReadNumber(value, "cloudCover"),
    precipitation: precipitation
      ? {
          probabilityPercent: safeReadNumber(
            precipitation,
            "probabilityPercent",
          ),
          type: safeReadString(precipitation, "type"),
          quantity: parseQuantity(precipitation.quantity),
        }
      : undefined,
    wind: wind
      ? {
          directionDegrees: safeReadNumber(wind, "directionDegrees"),
          directionCardinal: safeReadString(wind, "directionCardinal"),
          speed: parseQuantity(wind.speed),
          gust: parseQuantity(wind.gust),
        }
      : undefined,
    visibility: parseQuantity(value.visibility),
  };
}

export function formatClimateTemperature(
  value?: ClimateTemperature,
  unitSystem?: ClimateUnitSystem,
): string | undefined {
  if (!value) {
    return undefined;
  }
  const unit =
    unitSystem === undefined
      ? value.unit
      : unitSystem === "IMPERIAL"
        ? "FAHRENHEIT"
        : "CELSIUS";
  const degrees = convertTemperature(value.degrees, value.unit, unit);
  return `${degrees.toFixed(1)} °${unit === "CELSIUS" ? "C" : "F"}`;
}

export function getClimateTemperatureUnitSystem(
  selectedMetrics: readonly ClimateMetricSelection[] = DEFAULT_CLIMATE_METRICS,
): ClimateUnitSystem {
  return (
    selectedMetrics.find(({ metric }) => metric === "temperature")
      ?.unitSystem ?? "METRIC"
  );
}

export function formatClimateSummary(
  value: unknown,
  selectedMetrics?: readonly ClimateMetricSelection[],
  options: { includeAttribution?: boolean } = {},
): string {
  const climate = normalizeClimateValue(value);
  if (!climate) {
    return "";
  }

  const condition = climate.condition?.description;
  const summary = [
    formatClimateTemperature(
      climate.temperature,
      getClimateTemperatureUnitSystem(selectedMetrics),
    ),
    condition,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
  return options.includeAttribution === false
    ? summary
    : `${summary} · ${CLIMATE_ATTRIBUTION}`;
}

const QUANTITY_UNIT_LABELS: Record<string, string> = {
  KILOMETERS: "km",
  KILOMETERS_PER_HOUR: "km/h",
  MILES: "mi",
  MILES_PER_HOUR: "mph",
  MILLIMETERS: "mm",
  INCHES: "in",
};

const QUANTITY_UNIT_SYSTEMS = [
  { metric: "KILOMETERS", imperial: "MILES", ratio: 1.609344 },
  {
    metric: "KILOMETERS_PER_HOUR",
    imperial: "MILES_PER_HOUR",
    ratio: 1.609344,
  },
  { metric: "MILLIMETERS", imperial: "INCHES", ratio: 25.4 },
];

function formatQuantity(
  quantity: ClimateQuantity | undefined,
  unitSystem: ClimateUnitSystem,
): string | undefined {
  if (!quantity) {
    return undefined;
  }
  const units = QUANTITY_UNIT_SYSTEMS.find(
    ({ metric, imperial }) =>
      quantity.unit === metric || quantity.unit === imperial,
  );
  const unit = units
    ? unitSystem === "IMPERIAL"
      ? units.imperial
      : units.metric
    : quantity.unit;
  const value =
    units && quantity.unit !== unit
      ? unitSystem === "IMPERIAL"
        ? quantity.value / units.ratio
        : quantity.value * units.ratio
      : quantity.value;
  return `${value.toFixed(1)} ${QUANTITY_UNIT_LABELS[unit] || unit}`;
}

function formatPercent(value: number | undefined): string | undefined {
  return value === undefined ? undefined : `${value}%`;
}

export function getClimateMetrics(
  climate: ClimateValue,
  selectedMetrics: readonly ClimateMetricSelection[] = DEFAULT_CLIMATE_METRICS,
): { label: string; value: string }[] {
  const selections = new Map(
    selectedMetrics.map(({ metric, unitSystem }) => [metric, unitSystem]),
  );
  const units = (metric: ClimateMetric): ClimateUnitSystem =>
    selections.get(metric) ?? "METRIC";
  const values: Record<
    Exclude<ClimateMetric, "temperature">,
    string | undefined
  > = {
    feelsLikeTemperature: formatClimateTemperature(
      climate.feelsLikeTemperature,
      units("feelsLikeTemperature"),
    ),
    airPressureMillibars:
      climate.airPressureMillibars === undefined
        ? undefined
        : `${climate.airPressureMillibars.toFixed(1)} mb`,
    relativeHumidity: formatPercent(climate.relativeHumidity),
    dewPoint: formatClimateTemperature(climate.dewPoint, units("dewPoint")),
    heatIndex: formatClimateTemperature(climate.heatIndex, units("heatIndex")),
    windChill: formatClimateTemperature(climate.windChill, units("windChill")),
    wind:
      [
        climate.wind?.directionCardinal?.replaceAll("_", " ") ??
          (climate.wind?.directionDegrees === undefined
            ? undefined
            : `${climate.wind.directionDegrees}°`),
        formatQuantity(climate.wind?.speed, units("wind")),
      ]
        .filter(Boolean)
        .join(" ") || undefined,
    windGust: formatQuantity(climate.wind?.gust, units("windGust")),
    visibility: formatQuantity(climate.visibility, units("visibility")),
    cloudCover: formatPercent(climate.cloudCover),
    precipitation: formatQuantity(
      climate.precipitation?.quantity,
      units("precipitation"),
    ),
    precipitationProbability: formatPercent(
      climate.precipitation?.probabilityPercent,
    ),
    precipitationType: climate.precipitation?.type?.replaceAll("_", " "),
    thunderstormProbability: formatPercent(climate.thunderstormProbability),
    uvIndex: climate.uvIndex?.toString(),
    isDaytime:
      climate.isDaytime === undefined
        ? undefined
        : climate.isDaytime
          ? "Day"
          : "Night",
  };
  return [...selections.keys()].flatMap((key) => {
    if (key === "temperature") {
      return [];
    }
    const value = values[key];
    return typeof value === "string"
      ? [{ label: CLIMATE_METRIC_LABELS[key], value }]
      : [];
  });
}

/** Shared lines for notification emails and PDF exports. */
export function formatClimateLines(
  value: unknown,
  selectedMetrics?: readonly ClimateMetricSelection[],
): string[] {
  const climate = normalizeClimateValue(value);
  if (!climate) {
    return [];
  }
  return [
    formatClimateSummary(climate, selectedMetrics, {
      includeAttribution: false,
    }),
    ...getClimateMetrics(climate, selectedMetrics).map(
      (metric) => `${metric.label}: ${metric.value}`,
    ),
  ];
}
